package com.neurobs.js.aws

import org.scalajs.dom.{HTMLElement, document, window}
import slinky.core.ExternalComponent
import slinky.core.annotations.react

import scala.scalajs.js
import scala.scalajs.js.annotation.JSImport

object Amplify {

	@js.native
	@JSImport("aws-amplify", "Amplify")
	object Amplify extends js.Object {
		def configure(config: js.Object): Unit = js.native
	}

	trait JWT extends js.Object {
		def toString(): String
	}

	trait AuthTokens extends js.Object {
		val idToken: js.UndefOr[JWT]
		val accessToken: JWT
	}

	trait AuthSession extends js.Object {
		val tokens: js.UndefOr[AuthTokens]
	}

	trait FetchAuthSessionOptions extends js.Object {
		val forceRefresh: js.UndefOr[Boolean]
	}

	object FetchAuthSessionOptions {
		def apply(forceRefresh: Boolean): FetchAuthSessionOptions =
			js.Dynamic.literal(forceRefresh = forceRefresh).asInstanceOf[FetchAuthSessionOptions]
	}

	@js.native
	@JSImport("@aws-amplify/auth", JSImport.Default)
	object AmplifyAuth extends js.Object {
		def fetchAuthSession(): js.Promise[AuthSession] = js.native
		def fetchAuthSession(options: FetchAuthSessionOptions): js.Promise[AuthSession] = js.native
		def signOut(): js.Promise[Unit] = js.native
	}

	// Define the Hub interface as a global object
	@js.native
	@JSImport("@aws-amplify/core", "Hub")
	object Hub extends js.Object {
	  // Listen for events on a specific channel
	  def listen(channel: String, callback: js.Function1[js.Dynamic, Unit]): js.Function0[Unit] = js.native
	}

	@js.native
	trait HubPayload extends js.Object {
	  def event: String = js.native
	  def data: js.Any = js.native
	}

	@js.native
	@JSImport("@aws-amplify/ui-react", "Authenticator")
	object AuthenticatorRaw extends js.Object

	@react object Authenticator extends ExternalComponent {
		case class Props(hideSignUp: Int)
		override val component = AuthenticatorRaw
	}

	@js.native
	@JSImport("@aws-amplify/ui-react/styles.css", JSImport.Namespace)
	object AmplifyCss extends js.Object

	def configureAuthenticatorCSS(): Unit = {
		// The Amplify CSS is packaged with Webpack. Referencing this export causes the style-loader Webpack plugin
		// to insert the CSS in a style tag at the end of the head element.
		val amplifyCss = AmplifyCss

		// Since the Amplify CSS is inserted dynamically after page loading, reset the CSS variables it uses
		// to the values from our own CSS variables so the styles are consistent with our own controls
		val root = document.querySelector(selectors = ":root").asInstanceOf[HTMLElement]
		val rootStyles = window.getComputedStyle(document.documentElement)

		def copyVariables(from: String, to: String): Unit = {
			root.style.setProperty(propertyName = to, value = rootStyles.getPropertyValue(propertyName = from))
		}

		root.style.setProperty("--amplify-components-authenticator-router-box-shadow", "0px")
		root.style.setProperty("--amplify-components-authenticator-router-border-style", "none")
		root.style.setProperty("--amplify-components-authenticator-form-padding", "20px")
		root.style.setProperty("--amplify-components-authenticator-footer-padding-bottom", "0px")
		root.style.setProperty("--amplify-components-fieldcontrol-outline-style", "none")
		root.style.setProperty("--amplify-components-heading-3-font-size", "1.5rem")
		copyVariables(from = "--main-button-background-color-hover", to = "--amplify-components-button-primary-hover-background-color")
		copyVariables(from = "--main-button-background-color", to = "--amplify-components-button-primary-background-color")
		copyVariables(from = "--main-button-background-color-hover", to = "--amplify-components-button-primary-focus-background-color")

		copyVariables(from = "--main-text-input-border-color", to = "--amplify-components-fieldcontrol-border-color")
		copyVariables(from = "--main-text-input-border-radius", to = "--amplify-components-fieldcontrol-border-radius")
		copyVariables(from = "--main-text-input-border-style", to = "--amplify-components-fieldcontrol-border-style")
		copyVariables(from = "--main-text-input-border-width", to = "--amplify-components-fieldcontrol-border-width")
		copyVariables(from = "--main-text-input-border-color-focus", to = "--amplify-components-fieldcontrol-focus-border-color")
		copyVariables(from = "--main-text-input-box-shadow-focus", to = "--amplify-components-fieldcontrol-focus-box-shadow")
		copyVariables(from = "--main-text-input-padding", to = "--amplify-components-fieldcontrol-padding-block-start")
		copyVariables(from = "--main-text-input-padding", to = "--amplify-components-fieldcontrol-padding-block-end")
		copyVariables(from = "--main-text-input-padding", to = "--amplify-components-fieldcontrol-padding-inline-start")
		copyVariables(from = "--main-text-input-padding", to = "--amplify-components-fieldcontrol-padding-inline-end")

		copyVariables(from = "--main-text-color-default", to = "--amplify-components-heading-color")
		copyVariables(from = "--main-text-color-default", to = "--amplify-components-field-label-color")
	}
}
